import PageContainer from '../components/Wrappers';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import WebApp from '@twa-dev/sdk';
import { useEffectOnce } from 'react-use';
import { InvoiceStatuses } from '@twa-dev/types';
import { postShopLink, usePurchases } from '../lib/api';
import { IProduct, IPurchase, IVial } from '../lib/types';
import Loading from '../components/Loading';
import { doHapticFeedback } from '../lib/utils';
import Row from '../components/Row';
import BuyButton from '../components/Products/BuyButton';
import BuyCard from '../components/Products/BuyCard';
import IconStar from '../components/Icons/IconStar';

const IntroText = styled.div`
  width: 880px;

  font-family: Roboto, sans-serif;
  font-size: 35px;
  font-weight: 400;
  line-height: 40px;
  text-align: center;

  margin-bottom: 40px;
`;

const RightSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Label = styled.div`
  font-family: var(--font), sans-serif;
  font-size: 35px;
  font-weight: 400;
  line-height: 35px;
  text-align: left;
  color: #faff00;
  text-transform: uppercase;
`;

const Description = styled.div`
  font-family: Roboto, sans-serif;
  font-size: 35px;
  font-weight: 500;
`;

const StatsContainer = styled.div`
  position: relative;
  text-align: center;

  width: 690px;
  padding: 20px 80px 20px 80px;
  background: #232323;
  border-radius: 45px;
  border: 2px solid #a7a7a3;
  margin-bottom: 40px;
`;

const StatsLabel = styled.div`
  color: white;
  font-family: var(--font), sans-serif;
  font-size: 35px;
  font-weight: 400;
  line-height: 44.56px;
  text-align: left;
  text-transform: uppercase;
`;

const Value = styled.div`
  color: #faff00;
  font-family: var(--font), sans-serif;
  font-size: 35px;
  font-weight: 400;
  line-height: 44.56px;
  text-align: left;
`;

const Vials = () => {
  const { updateApp, setEvent, app } = useApp();
  const { t } = useTranslation();
  const [count, setCount] = useState(1);
  const [isProcessing, setIsProcessing] = useState<null | string>(null);
  const { data: purchases, isLoading } = usePurchases(app.state.extraFuelDuration);

  const handleBuy = async (product: IProduct) => {
    if (isProcessing) return;
    doHapticFeedback();
    setIsProcessing(product.id);
    setCount(product.virtualFriendsCount);
    const invoiceUrl = await postShopLink(product.id);
    WebApp.openInvoice(invoiceUrl.link);
  };

  useEffectOnce(() => {
    const handleInvoiceClosed = ({ status }: { status: InvoiceStatuses }) => {
      if (status === 'paid') {
        setEvent({ type: 'purchased', product: 'vial', count });
        setTimeout(updateApp, 5000);
      }
      setIsProcessing(null);
    };

    WebApp.onEvent('invoiceClosed', handleInvoiceClosed);
    return () => {
      WebApp.offEvent('invoiceClosed', handleInvoiceClosed);
    };
  });

  const products = app.config.shop.products.filter((product) => product.type === 'boosters') as IVial[];

  if (isLoading)
    return (
      <PageContainer vcenter>
        <Loading fontSize="100px" />
      </PageContainer>
    );

  return (
    <PageContainer vcenter>
      {!app.state.extraFuelDuration && <IntroText>{t('boosts.vials.intro')}</IntroText>}
      {!!app.state.extraFuelDuration && (
        <StatsContainer>
          <Row spread={true}>
            <StatsLabel>{t('boosts.vials.extraHoursCount')}</StatsLabel>
            <Value>{app.state.extraFuelDuration / 3600}</Value>
          </Row>
        </StatsContainer>
      )}

      {products.map((product) => {
        const isProductProcessing = isProcessing && isProcessing === product.id;
        const vialsCount = product.fuelDuration / 3600;
        const boughtCount = purchases.filter((purchase: IPurchase) => purchase.productId === product.id).length;
        const disabled = boughtCount >= product.maxCount;

        return (
          <BuyCard key={product.id}>
            <img alt="spirit" src={`/img/vials/vial_x${vialsCount}.png`} />
            <RightSide>
              <Label>
                {vialsCount} {t('boosts.vials.vialOfMentalEnergy', { count: vialsCount })}
              </Label>
              <Description>
                {vialsCount} {t('boosts.vials.extraHour', { count: vialsCount })}
              </Description>
              <BuyButton onClick={() => handleBuy(product)} disabled={isLoading || isProcessing || disabled}>
                {!isProductProcessing && (
                  <>
                    {t('friends.spirit.buyFor')} {product.totalAmount} <IconStar />
                  </>
                )}
                {isProductProcessing && <Loading />}
              </BuyButton>
            </RightSide>
          </BuyCard>
        );
      })}
    </PageContainer>
  );
};

export default Vials;
